import React from 'react';
import styled from 'styled-components';
import Icon from '../Icon';
import Typo from '../Typo';
import theme from '../theme';
import { getFileIcon, formatFileSize } from '../../utils/fileTypes';
import { PROVIDER_TYPES } from '../../helpers/media';

// Provider names and base URLs for external embeds
const providerInfo = {
  [PROVIDER_TYPES.YOUTUBE]: { name: 'YouTube', baseUrl: 'https://youtube.com/watch?v=' },
  [PROVIDER_TYPES.VIMEO]: { name: 'Vimeo', baseUrl: 'https://vimeo.com/' },
  [PROVIDER_TYPES.SOUNDCLOUD]: { name: 'SoundCloud', baseUrl: '' },
  [PROVIDER_TYPES.SPOTIFY]: { name: 'Spotify', baseUrl: 'https://open.spotify.com/track/' },
  [PROVIDER_TYPES.WISTIA]: { name: 'Wistia', baseUrl: 'https://wistia.com/medias/' },
  [PROVIDER_TYPES.YOUKU]: { name: 'Youku', baseUrl: 'https://v.youku.com/v_show/id_' },
  [PROVIDER_TYPES.LINK]: { name: 'Link', baseUrl: '' },
  [PROVIDER_TYPES.EMBED]: { name: 'Embed', baseUrl: '' },
};

/**
 * Get display URL and provider name from external object
 * @param {Object} external - External object with { id, provider }
 * @returns {{ url: string, providerName: string } | null}
 */
const getExternalUrlInfo = (external) => {
  if (!external || !external.provider) return null;

  const info = providerInfo[external.provider];
  if (!info) return null;

  // LINK provider means the id IS the full URL
  // EMBED provider means it's raw embed code (iframe)
  if (external.provider === PROVIDER_TYPES.LINK) {
    return { url: external.id, providerName: info.name };
  }

  if (external.provider === PROVIDER_TYPES.EMBED) {
    // Try to extract URL from iframe if possible
    const iframeMatch = external.id?.match?.(/src=["']([^"']+)["']/i);
    return { url: iframeMatch ? iframeMatch[1] : null, providerName: info.name };
  }

  // For known providers, construct the URL
  const url = info.baseUrl ? `${info.baseUrl}${external.id}` : null;
  return { url, providerName: info.name };
};

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '4px' : '8px')};
  padding: ${({ $compact }) => ($compact ? '2px 6px' : '8px')};
  background: ${({ $error }) => ($error ? theme.colors.ERROR_LIGHT : theme.colors.NEU_50)};
  border: 1px solid ${({ $error }) => ($error ? theme.colors.ERROR_RED : theme.colors.NEU_300)};
  border-radius: ${({ $compact }) => ($compact ? '8px' : '12px')};
`;

const ErrorText = styled.span`
  color: ${theme.colors.ERROR_RED};
  font-size: 12px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? '8px' : '16px')};
`;

const FilePreviewImage = styled.img`
  width: ${({ $compact }) => ($compact ? '30px' : '44px')};
  height: ${({ $compact }) => ($compact ? '30px' : '44px')};
  object-fit: cover;
  border-radius: ${({ $compact }) => ($compact ? '4px' : '8px')};
  background: ${theme.colors.NEU_100};
`;

const FilePreviewIcon = styled.div`
  width: ${({ $compact }) => ($compact ? '30px' : '44px')};
  height: ${({ $compact }) => ($compact ? '30px' : '44px')};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $compact }) => ($compact ? '4px' : '8px')};
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ExternalUrl = styled.a`
  color: ${theme.colors.NEU_600};
  font-size: 12px;
  text-decoration: none;
  word-break: break-all;
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
    color: ${theme.colors.PRIMARY};
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ $compact }) => ($compact ? '4px' : '8px')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.colors.NEU_100};
  }
`;

/**
 * FilePreview - Displays a preview of a file with remove button and optional children content
 *
 * @param {Object} props
 * @param {Object} [props.file] - File information object (optional if external is provided)
 * @param {string} props.file.id - Unique identifier for the file
 * @param {string} props.file.name - File name to display
 * @param {string} props.file.type - MIME type of the file (e.g., 'image/jpeg')
 * @param {number} [props.file.size] - File size in bytes (optional)
 * @param {string} [props.file.url] - URL to the file (optional)
 * @param {string} [props.file.previewUrl] - URL for thumbnail preview (optional, used for images)
 * @param {Object} [props.external] - External embed data { id, provider } (e.g., YouTube, Vimeo)
 * @param {string} props.external.id - Provider-specific ID or URL
 * @param {number} props.external.provider - Provider type (1=YouTube, 2=Vimeo, 4=Spotify, 7=Link, etc.)
 * @param {Function} props.onRemove - Callback when remove button is clicked (called directly, no args)
 * @param {boolean} [props.compact] - Use compact variant with smaller thumbnail (30x30) and padding
 * @param {string} [props.error] - Error message to display (shows error styling when set)
 * @param {React.ReactNode} [props.children] - Optional content to render below the file info (e.g., caption input)
 */
const FilePreview = ({ file, external, onRemove, compact = false, error, children }) => {
  // Handle external URL case (from params.external or from file.data.external for initial files)
  const externalData = external || file?.data?.external || file?.external;
  const externalInfo = getExternalUrlInfo(externalData);

  // Handle URL type files (from URL input in uploader)
  const isUrlFile = file?.isUrl || file?.type === 'url';
  const urlFromFile = isUrlFile ? file?.data?.url || file?.url : null;

  if (!file && !externalInfo) return null;

  const iconSize = compact ? 'small' : 'medium';

  // URL/External mode - both external embeds and URL input files show as links
  if (externalInfo || (isUrlFile && urlFromFile)) {
    const displayName = externalInfo?.providerName || file?.name || 'Link';
    const displayUrl = externalInfo?.url || urlFromFile;
    const iconColor = error ? theme.colors.ERROR_RED : theme.colors.PRIMARY;

    return (
      <PreviewContainer $compact={compact} $error={!!error}>
        <FileRow $compact={compact}>
          <FilePreviewIcon $compact={compact}>
            <Icon.Link size={iconSize} color={iconColor} />
          </FilePreviewIcon>
          <FileInfo>
            <Typo.SmallText fontWeight='600'>{displayName}</Typo.SmallText>
            {displayUrl && (
              <ExternalUrl href={displayUrl} target='_blank' rel='noopener noreferrer'>
                {displayUrl}
              </ExternalUrl>
            )}
            {error && <ErrorText>{error}</ErrorText>}
          </FileInfo>
          {onRemove && (
            <RemoveButton $compact={compact} onClick={onRemove} title='Remove' type='button' aria-label='Remove'>
              <Icon.Close size='small' color={theme.colors.NEU_600} />
            </RemoveButton>
          )}
        </FileRow>
        {children}
      </PreviewContainer>
    );
  }

  // File mode
  const fileType = file.mime || file.type || '';
  const IconComponent = Icon[getFileIcon(fileType)] || Icon.File;
  const isImage = fileType?.startsWith?.('image/');
  const thumbnailUrl = file.previewUrl || file.url;
  const fileName = file.name || file.title || 'File';

  const getSubtitle = () => {
    if (file.size) return formatFileSize(file.size);
    return '';
  };

  return (
    <PreviewContainer $compact={compact}>
      <FileRow $compact={compact}>
        {thumbnailUrl && isImage ? (
          <FilePreviewImage $compact={compact} src={thumbnailUrl} alt={fileName} />
        ) : (
          <FilePreviewIcon $compact={compact}>
            <IconComponent size={iconSize} color={theme.colors.NEU_600} />
          </FilePreviewIcon>
        )}
        <FileInfo>
          <Typo.SmallText fontWeight='600'>{fileName}</Typo.SmallText>
          {!compact && getSubtitle() && <Typo.Info color={theme.colors.NEU_600}>{getSubtitle()}</Typo.Info>}
        </FileInfo>
        {onRemove && (
          <RemoveButton $compact={compact} onClick={onRemove} title='Remove' type='button' aria-label='Remove file'>
            <Icon.Close size='small' color={theme.colors.NEU_600} />
          </RemoveButton>
        )}
      </FileRow>
      {children}
    </PreviewContainer>
  );
};

export default FilePreview;
